import { trimDecimals } from '@einhaus/meteoric-helpers';
import type {
    EntityOptionMetadata,
    EntityOptionObjectCompressed,
    EvaluatedBacktestTrade,
    SimulateBacktestTradesStats
} from '@einhaus/project314-types';
import { getPerformanceForTrades as getRawPerformanceForTrades } from '@/utils/getPerformanceForTrades';
import { getTradeUnderlyingBiasSummary, type UnderlyingBiasSummary } from '@/utils/tradeSemantics';

export type EntityStatsGrouping = 'all' | 'long' | 'short' | UnderlyingBiasSummary;
type TradeWithExposure = EvaluatedBacktestTrade & Parameters<typeof getTradeUnderlyingBiasSummary>[0];

export const underlyingExposureGroups: readonly UnderlyingBiasSummary[] = ['bullish', 'bearish', 'mixed', 'unknown'];

export function getPortfolioBacktestOptionRequests(trades: readonly TradeWithExposure[]): EntityOptionObjectCompressed[] {
    const requests = new Map<string, EntityOptionObjectCompressed>();
    for (const trade of trades) {
        if (trade.entryContext?.portfolioTradeAdmissionSnapshot?.positions.length) continue;
        for (const transaction of trade.transactions) {
            if (!transaction.isOpeningTrade || transaction.derivativeType !== 'Option' || transaction.derivativeId == null) continue;
            if (transaction.entityType !== 'Asset' && transaction.entityType !== 'AssetIndex') continue;
            requests.set(`${transaction.entityType}:${transaction.entityId}:${transaction.derivativeId}`, {
                eId: transaction.entityId,
                eType: transaction.entityType,
                oId: transaction.derivativeId
            });
        }
    }
    return [...requests.values()];
}

export function groupPortfolioBacktestTrades<T extends TradeWithExposure>(
    trades: T[],
    options: readonly Pick<EntityOptionMetadata, 'id' | 'type' | 'assetId' | 'entityType'>[]
): Record<EntityStatsGrouping, T[]> {
    const groups: Record<EntityStatsGrouping, T[]> = { all: trades, long: [], short: [], bullish: [], bearish: [], mixed: [], unknown: [] };
    for (const trade of trades) {
        groups[getTradeUnderlyingBiasSummary(trade, options)].push(trade);
        const side = trade.transactions[0]?.direction;
        if (side === 'long' || side === 'short') groups[side].push(trade);
    }
    return groups;
}

// Accepted portfolio trades must not pass through the single-strategy overlap filter again.
export const getPortfolioBacktestPerformance = (config: Parameters<typeof getRawPerformanceForTrades>[0]): SimulateBacktestTradesStats =>
    getRawPerformanceForTrades({ ...config, skipOverlappingTrades: false });

export interface PerformanceStatsRow {
    startingBalance: string;
    endingBalance: string;
    numberOfTrades: number;
    avgProfitPercentage: number | null;
    profit: number | null;
    profitPercentage: number | null;
    arorc: number | null;
    averageRiskCapital: number | null;
    averagePositions: number | null;
}

export function buildPerformanceStatsRow(stats: SimulateBacktestTradesStats): PerformanceStatsRow {
    const finiteValue = (value: number): number | null => (Number.isFinite(value) ? trimDecimals(value) : null);
    const hasTrades = stats.numberOfTrades > 0;
    return {
        startingBalance: `$${stats.startingBalance}`,
        endingBalance: `$${stats.endingBalance}`,
        numberOfTrades: stats.numberOfTrades,
        avgProfitPercentage: hasTrades ? finiteValue(stats.avgTradeProfitPercentage) : null,
        profit: finiteValue(stats.profit),
        profitPercentage: finiteValue(stats.profitPercentage),
        arorc: hasTrades ? finiteValue(stats.returnOnRiskCapital) : null,
        averageRiskCapital: hasTrades ? finiteValue(stats.averageRiskCapitalPerDay) : null,
        averagePositions: hasTrades ? finiteValue(stats.averagePositions) : null
    };
}
